/**
 * SessionHydrator — Client-side personalization layer.
 *
 * The server always renders a guest-only HTML shell (no user-specific data).
 * This module runs on every page load and:
 * 1. Fetches session data from /session-api/me  (single AJAX call)
 * 2. Hydrates the header account section (avatar, name, dropdown)
 * 3. Updates cart badge count
 * 4. Updates wishlist badge count & button states on product cards
 * 5. Stores session in sessionStorage for instant hydration on subsequent
 *    navigations within the same tab.
 *
 * Design decisions:
 * - Uses fetch() (no jQuery dependency) so it can boot before jQuery is ready.
 * - Falls back gracefully if the endpoint is unreachable.
 * - Dispatches `session:hydrated` CustomEvent so other modules can react.
 */

const SESSION_CACHE_KEY = "ov_session_cache";
const SESSION_CACHE_TTL_MS = 60_000; // 1 minute

class SessionHydrator {
  constructor() {
    this.apiEndpoint = "/session-api/me";
    this.session = null;
    this._hydrated = false;
    /** @type {string[]|null} innerHTML of each `.header-bar-account` as sent by server (guest shell) */
    this._guestAccountSnapshots = null;
  }

  // ────────────────────────────────────────────────────────
  //  Public API
  // ────────────────────────────────────────────────────────

  /**
   * Boot the hydrator. Should be called as early as possible in main.js.
   */
  async boot() {
    try {
      // Capture guest markup before applying sessionStorage — after login the
      // DOM is still the server guest shell, but stale cache may re-apply a
      // logged-in header; when /session-api/me later returns guest we must
      // restore this HTML (see _hydrateHeaderAccount).
      this._captureGuestAccountMarkup();

      this.session = this._readCache();

      if (this.session) {
        // Apply instantly from cache, then refresh in background
        this._applyAll(this.session);
        this._fetchAndApply(); // non-blocking refresh
      } else {
        await this._fetchAndApply();
      }
    } catch (err) {
      console.warn("[SessionHydrator] boot failed:", err);
    }
  }

  /**
   * Force-refresh session data (e.g. after login/logout/cart update).
   */
  async refresh() {
    this._clearCache();
    await this._fetchAndApply();
  }

  /**
   * Return cached session (may be null before boot completes).
   */
  getSession() {
    return this.session;
  }

  // ────────────────────────────────────────────────────────
  //  Fetch
  // ────────────────────────────────────────────────────────

  async _fetchAndApply() {
    try {
      const res = await fetch(this.apiEndpoint, {
        method: "GET",
        headers: {
          "X-Requested-With": "XMLHttpRequest",
          Accept: "application/json",
        },
        credentials: "same-origin",
      });

      if (!res.ok) return;

      const json = await res.json();
      if (!json.success || !json.data) return;

      this.session = json.data;
      this._writeCache(this.session);
      this._applyAll(this.session);
    } catch (err) {
      console.warn("[SessionHydrator] fetch failed:", err);
    }
  }

  // ────────────────────────────────────────────────────────
  //  Apply helpers
  // ────────────────────────────────────────────────────────

  _applyAll(s) {
    this._hydrateHeaderAccount(s);
    this._hydrateCartBadge(s);
    this._hydrateWishlistBadge(s);
    this._hydrateWishlistButtons(s);
    this._hydratePrices(s);
    this._hydrated = true;

    // Notify other modules
    window.dispatchEvent(
      new CustomEvent("session:hydrated", { detail: s })
    );
  }

  // ── Header Account ──────────────────────────────────────

  _hydrateHeaderAccount(s) {
    const containers = document.querySelectorAll(".header-bar-account");
    if (!containers.length) return;

    containers.forEach((container, index) => {
      if (s.loggedIn && s.user) {
        this._renderLoggedInAccount(container, s.user, s.affiliateShowLink);
      } else {
        this._renderGuestAccount(container, index);
      }
    });
  }

  _captureGuestAccountMarkup() {
    const containers = document.querySelectorAll(".header-bar-account");
    if (!containers.length) {
      this._guestAccountSnapshots = null;
      return;
    }
    this._guestAccountSnapshots = Array.from(containers).map(
      (el) => el.innerHTML
    );
  }

  _renderGuestAccount(container, index) {
    const snap =
      this._guestAccountSnapshots?.[index] ??
      this._guestAccountSnapshots?.[0];
    if (!snap) return;
    container.innerHTML = snap;
    container.classList.add("group");
  }

  _renderLoggedInAccount(container, user, affiliateShowLink) {
    const langUrl = (path) => window.storefrontLangUrl ? window.storefrontLangUrl(path) : path;

    // Build avatar HTML
    const avatarHtml = user.avatar
      ? `<img src="${this._escapeHtml(user.avatar)}" alt="" class="rounded-full w-8 h-8 object-cover" />`
      : `<span class="flex items-center justify-center w-8 h-8 rounded-full bg-brand text-white text-sm font-medium">${this._escapeHtml((user.fullname || "U").charAt(0).toUpperCase())}</span>`;

    // Build affiliate link
    const affiliateLink = affiliateShowLink
      ? `<li class="pt-2 border-t border-gray-100">
           <a href="${langUrl("/affiliate")}" class="flex items-center space-x-2 hover:text-brand py-2">
             <span>${window.__STOREFRONT_I18N__?.account?.dropdown?.affiliate || 'Tiếp thị liên kết (Affiliate)'}</span>
           </a>
         </li>`
      : "";

    container.innerHTML = `
      <a href="${langUrl("/account")}" class="flex items-center header-bar-account-name">${avatarHtml}</a>
      <div class="header-bar-account-dropdown">
        <ul class="space-y-2">
          <li class="pb-2 border-b border-gray-100">
            <a href="${langUrl("/account")}" class="flex flex-col space-y-2 hover:text-brand">
              <span class="font-medium">${this._escapeHtml(user.fullname)}</span>
              <span class="text-sm text-gray-500">${this._escapeHtml(user.phone)}</span>
            </a>
          </li>
          <li>
            <a href="${langUrl("/account/profile")}" class="flex items-center space-x-2 hover:text-brand py-2">
              <span>${window.__STOREFRONT_I18N__?.account?.dropdown?.profile || 'Thông tin tài khoản'}</span>
            </a>
          </li>
          <li>
            <a href="${langUrl("/account/orders")}" class="flex items-center space-x-2 hover:text-brand py-2">
              <span>${window.__STOREFRONT_I18N__?.account?.dropdown?.orders || 'Lịch sử mua hàng'}</span>
            </a>
          </li>
          <li>
            <a href="${langUrl("/account/wishlist")}" class="flex items-center space-x-2 hover:text-brand py-2">
              <span>${window.__STOREFRONT_I18N__?.account?.dropdown?.wishlist || 'Sản phẩm yêu thích'}</span>
            </a>
          </li>
          ${affiliateLink}
          <li class="pt-2 border-t border-gray-100">
            <a href="${langUrl("/account/logout")}" class="flex items-center space-x-2 text-red-600 hover:text-red-700 py-2">
              <span>${window.__STOREFRONT_I18N__?.account?.dropdown?.logout || 'Thoát'}</span>
            </a>
          </li>
        </ul>
      </div>`;

    // Re-add the "group" class to ensure hover dropdown works
    container.classList.add("group");
  }

  // ── Cart Badge ──────────────────────────────────────────

  _hydrateCartBadge(s) {
    const badges = document.querySelectorAll(".cart-quantity-badge");
    badges.forEach((badge) => {
      if (s.cartCount > 0) {
        badge.textContent = s.cartCount;
        badge.style.display = "inline";
        badge.classList.remove("bg-gray-100", "text-gray-300");
        badge.classList.add("bg-red-500", "text-white");
      } else {
        badge.style.display = "none";
      }
    });
  }

  // ── Wishlist Badge ──────────────────────────────────────

  _hydrateWishlistBadge(s) {
    const count = (s.wishlistProductIds || []).length;
    const badges = document.querySelectorAll(".wishlist-quantity-badge");
    badges.forEach((badge) => {
      if (count > 0) {
        badge.textContent = count;
        badge.style.display = "inline";
      } else {
        badge.style.display = "none";
      }
    });
  }

  // ── Wishlist Buttons on Product Cards ───────────────────

  _hydrateWishlistButtons(s) {
    const wishlistIds = new Set(s.wishlistProductIds || []);
    const buttons = document.querySelectorAll(".add-to-wishlist-btn");

    if (wishlistIds.size === 0) {
      buttons.forEach((btn) => {
        btn.classList.remove("is-wishlisted");
        btn.classList.add("text-gray-600");
        btn.classList.remove("text-red-500");
        const svg = btn.querySelector("svg");
        if (svg) svg.removeAttribute("fill");
      });
      return;
    }

    buttons.forEach((btn) => {
      const productId = parseInt(btn.dataset.productId, 10);
      if (wishlistIds.has(productId)) {
        // Mark as wishlisted
        btn.classList.add("is-wishlisted");
        btn.classList.remove("text-gray-600");
        btn.classList.add("text-red-500");

        // Fill the heart SVG
        const svg = btn.querySelector("svg");
        if (svg) {
          svg.setAttribute("fill", "currentColor");
        }
      }
    });
  }

  // ── Smart Price Hydration ────────────────────────────────
  // Only fetches personalized prices when user's price policy
  // differs from the default one rendered in cached HTML.

  _hydratePrices(s) {
    // Skip if not logged in or same price policy as default
    if (!s.loggedIn) return;
    if (s.pricePolicyId === s.websitePricePolicyId) return;

    // Collect product IDs from visible product cards
    const cards = document.querySelectorAll(".product-card[data-product-id]");
    if (!cards.length) return;

    const productIds = [];
    cards.forEach((card) => {
      const id = parseInt(card.dataset.productId, 10);
      if (id > 0) productIds.push(id);
    });

    if (productIds.length === 0) return;

    // Fetch personalized prices
    fetch("/product/pricesajax", {
      method: "POST",
      credentials: "same-origin",
      headers: {
        "Content-Type": "application/json",
        "X-Requested-With": "XMLHttpRequest",
      },
      body: JSON.stringify({ product_ids: productIds }),
    })
      .then((res) => (res.ok ? res.json() : null))
      .then((json) => {
        if (!json?.success || !json?.data?.prices) return;
        this._updatePricesInDOM(json.data.prices);
      })
      .catch(() => {});
  }

  _updatePricesInDOM(prices) {
    // prices = { productId: { sell_price, base_price, formatted_sell, formatted_base } }
    Object.entries(prices).forEach(([productId, priceData]) => {
      const card = document.querySelector(
        `.product-card[data-product-id="${productId}"]`
      );
      if (!card) return;

      const pricesContainer = card.querySelector(".product-card-prices");
      if (!pricesContainer) return;

      const sellPrice = priceData.sell_price || 0;
      const basePrice = priceData.base_price || 0;

      if (sellPrice <= 0) return; // Keep "Liên hệ" as-is

      let html = "";
      if (basePrice > 0 && sellPrice < basePrice) {
        html += `<span class="product-price-old">${priceData.formatted_base}</span>`;
      }
      html += `<span class="product-price">${priceData.formatted_sell}</span>`;

      pricesContainer.innerHTML = html;
    });
  }

  // ────────────────────────────────────────────────────────
  //  Cache (sessionStorage)
  // ────────────────────────────────────────────────────────

  _readCache() {
    try {
      const raw = window.sessionStorage.getItem(SESSION_CACHE_KEY);
      if (!raw) return null;
      const { data, ts } = JSON.parse(raw);
      if (Date.now() - ts > SESSION_CACHE_TTL_MS) {
        window.sessionStorage.removeItem(SESSION_CACHE_KEY);
        return null;
      }
      return data;
    } catch {
      return null;
    }
  }

  _writeCache(data) {
    try {
      window.sessionStorage.setItem(
        SESSION_CACHE_KEY,
        JSON.stringify({ data, ts: Date.now() })
      );
    } catch {
      // Ignore quota errors
    }
  }

  _clearCache() {
    try {
      window.sessionStorage.removeItem(SESSION_CACHE_KEY);
    } catch {
      // Ignore
    }
  }

  // ────────────────────────────────────────────────────────
  //  Utils
  // ────────────────────────────────────────────────────────

  _escapeHtml(str) {
    const div = document.createElement("div");
    div.textContent = str || "";
    return div.innerHTML;
  }
}

// Singleton instance
const sessionHydrator = new SessionHydrator();

// Export for use in main.js and other modules
export default sessionHydrator;
export { SessionHydrator };
